import React from "react";
import Alert from "@mui/material/Alert";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SelectFarmWarehouse from "../../../components/SelectFarmWarehouse";
import { getAPIDataParamsReturn } from "../../../utils/apiFunction";
import { numberFormatErrorToZero as numberFormat } from "../../../utils/numberFunctions";
import Switch from "@mui/material/Switch";
import {
    buildOysterMateTotalsData,
    SLATS,
    formatOysterMateQuantity,
    getBatchBackgroundColor,
    getOysterMateGradeColor,
    mergeOysterMateGroups,
} from "../../Farms/oysterMateTotalsUtils";

const getHatcheryLiveStockTypeCode = (group) => {
    if (group?.grade?.speciesId === 2) {
        return "PAC";
    }

    return group?.isHatched === true ? "HAT" : "WLD";
};

const isMixedSupplierGroup = (group) => {
    const supplierName = `${group?.supplier?.name || group?.supplierName || ""}`.trim().toLowerCase();
    return group?.isMixed === true || supplierName === "mixed suppliers";
};

const matchesHatcheryLiveFilters = (group, filters) => {
    if (!group) {
        return false;
    }

    if (filters?.cropId && `${group.cropId ?? ""}` !== filters.cropId) {
        return false;
    }

    if (filters?.supplierId) {
        if (filters.supplierId === "__MIXED__") {
            if (!isMixedSupplierGroup(group)) {
                return false;
            }
        } else if (isMixedSupplierGroup(group) || `${group.supplierId ?? ""}` !== filters.supplierId) {
            return false;
        }
    }

    if (filters?.stockType && getHatcheryLiveStockTypeCode(group) !== filters.stockType) {
        return false;
    }

    if (filters?.cropDate) {
        const cropDateValue = `${group.cropDate ?? ""}`;
        if (!cropDateValue || cropDateValue.slice(0, 10) !== filters.cropDate) {
            return false;
        }
    }

    return true;
};

const fetchHatcheryLiveStockGroups = async (warehouseId, filters, abortController) => {
    const [waterGroups, onShoreGroups] = await Promise.all([
        getAPIDataParamsReturn("/Farms/Groups", { WarehouseId: warehouseId }, abortController),
        getAPIDataParamsReturn("/Farms/Groups/OnShore", { WarehouseId: warehouseId }, abortController),
    ]);

    return mergeOysterMateGroups(waterGroups, onShoreGroups).filter((group) => matchesHatcheryLiveFilters(group, filters));
};

const buildHatcheryLiveStockRows = (groups) => {
    return buildOysterMateTotalsData(groups, "By Batch").filter((row) => row.totalEnd > 0);
};

const NULL_FILTER_TOKEN = "__NULL__";

const emptyReport = {
    selectedGroupCount: 0,
    broodstock: [],
    leaseStages: [],
    onshoreStage: null,
    transferBreakdown: [],
    transferGradeTotals: [],
};

const emptyFilters = {
    cropId: "",
    supplierId: "",
    stockType: "",
    cropDate: "",
};

const emptyFilterBatches = [];

const emptyFilterOptions = {
    crops: [],
    suppliers: [],
    stockTypes: [],
    cropDates: [],
};

const normaliseStringOptions = (values, preserveNullOption = false) => {
    if (!Array.isArray(values)) {
        return [];
    }

    const seen = new Set();

    return values.reduce((accumulator, value) => {
        const nextValue = value == null || `${value}`.trim() === ""
            ? (preserveNullOption ? NULL_FILTER_TOKEN : null)
            : `${value}`;

        if (!nextValue || seen.has(nextValue)) {
            return accumulator;
        }

        seen.add(nextValue);
        accumulator.push(nextValue);
        return accumulator;
    }, []);
};

const sortCropOptions = (values) => {
    return [...values].sort((left, right) => {
        const leftIsNull = left.id === NULL_FILTER_TOKEN;
        const rightIsNull = right.id === NULL_FILTER_TOKEN;

        if (leftIsNull !== rightIsNull) {
            return leftIsNull ? 1 : -1;
        }

        return `${left.name}`.localeCompare(`${right.name}`, undefined, { numeric: true, sensitivity: "base" });
    });
};

const normaliseBatchOptions = (values) => {
    if (!Array.isArray(values)) {
        return [];
    }

    const seen = new Set();

    return values.reduce((accumulator, option) => {
        if (!option) {
            return accumulator;
        }

        const cropId = option.cropId == null || `${option.cropId}`.trim() === ""
            ? NULL_FILTER_TOKEN
            : `${option.cropId}`.trim();
        const cropName = option.cropName == null || `${option.cropName}`.trim() === ""
            ? (cropId === NULL_FILTER_TOKEN ? "No crop" : `Crop ${cropId}`)
            : `${option.cropName}`.trim();
        const supplierId = option.supplierId == null ? "" : `${option.supplierId}`;
        const supplierKey = option.supplierKey == null || `${option.supplierKey}`.trim() === ""
            ? supplierId
            : `${option.supplierKey}`.trim();
        const stockType = option.stockType == null ? "" : `${option.stockType}`.trim();
        const stockTypeLabel = option.stockTypeLabel || stockType;
        const cropDate = option.cropDate == null ? "" : `${option.cropDate}`.trim();

        if (!supplierKey || !stockType || !cropDate) {
            return accumulator;
        }

        const key = `${cropId}|${supplierKey}|${stockType}|${cropDate}`;
        if (seen.has(key)) {
            return accumulator;
        }

        seen.add(key);

        accumulator.push({
            batch: option.batch || "",
            cropId,
            cropName,
            supplierId: supplierKey,
            supplierName: option.supplierName || "Unnamed supplier",
            stockType,
            stockTypeLabel,
            cropDate,
        });

        return accumulator;
    }, []);
};

const matchesBatchFilters = (batch, filters, ignoredField) => {
    if (!batch) {
        return false;
    }

    return ["cropId", "supplierId", "stockType", "cropDate"].every((field) => {
        if (field === ignoredField) {
            return true;
        }

        return !filters[field] || batch[field] === filters[field];
    });
};

const buildSupplierOptions = (batches) => {
    const suppliers = new Map();

    batches.forEach((batch) => {
        if (!batch?.supplierId) {
            return;
        }

        const nextName = batch.supplierName || "Unnamed supplier";
        const existing = suppliers.get(batch.supplierId);

        if (!existing || (existing.name !== "Mixed Suppliers" && nextName === "Mixed Suppliers")) {
            suppliers.set(batch.supplierId, { id: batch.supplierId, name: nextName });
        }
    });

    return Array.from(suppliers.values()).sort((left, right) => left.name.localeCompare(right.name));
};

const buildStockTypeOptions = (batches) => {
    const stockTypes = new Map();
    const getSortOrder = (value) => (value === "WLD" ? 0 : value === "HAT" ? 1 : 2);

    batches.forEach((batch) => {
        if (!batch?.stockType || stockTypes.has(batch.stockType)) {
            return;
        }

        stockTypes.set(batch.stockType, {
            id: batch.stockType,
            name: batch.stockTypeLabel || batch.stockType,
        });
    });

    return Array.from(stockTypes.values()).sort((left, right) => getSortOrder(left.id) - getSortOrder(right.id));
};

const buildFilterOptions = (batches, filters) => {
    if (!Array.isArray(batches) || batches.length === 0) {
        return emptyFilterOptions;
    }

    const getMatchingBatches = (ignoredField) => batches.filter((batch) => matchesBatchFilters(batch, filters, ignoredField));

    const cropMap = new Map();
    getMatchingBatches("cropId").forEach((batch) => {
        if (!batch?.cropId || cropMap.has(batch.cropId)) {
            return;
        }

        cropMap.set(batch.cropId, {
            id: batch.cropId,
            name: batch.cropName || (batch.cropId === NULL_FILTER_TOKEN ? "No crop" : `Crop ${batch.cropId}`),
        });
    });

    return {
        crops: sortCropOptions(Array.from(cropMap.values())),
        suppliers: buildSupplierOptions(getMatchingBatches("supplierId")),
        stockTypes: buildStockTypeOptions(getMatchingBatches("stockType")),
        cropDates: normaliseStringOptions(getMatchingBatches("cropDate").map((batch) => batch.cropDate)),
    };
};

const sanitiseFilters = (filters, filterOptions) => {
    const nextFilters = { ...filters };

    if (nextFilters.cropId && !filterOptions.crops.some((option) => option.id === nextFilters.cropId)) {
        nextFilters.cropId = "";
    }

    if (nextFilters.supplierId && !filterOptions.suppliers.some((option) => option.id === nextFilters.supplierId)) {
        nextFilters.supplierId = "";
    }

    if (nextFilters.stockType && !filterOptions.stockTypes.some((option) => option.id === nextFilters.stockType)) {
        nextFilters.stockType = "";
    }

    if (nextFilters.cropDate && !filterOptions.cropDates.includes(nextFilters.cropDate)) {
        nextFilters.cropDate = "";
    }

    return nextFilters;
};

const areFiltersEqual = (left, right) => (
    left.cropId === right.cropId
    && left.supplierId === right.supplierId
    && left.stockType === right.stockType
    && left.cropDate === right.cropDate
);

const resolveFilterState = (batches, filters) => {
    let nextFilters = { ...filters };

    while (true) {
        const nextOptions = buildFilterOptions(batches, nextFilters);
        const sanitised = sanitiseFilters(nextFilters, nextOptions);

        if (areFiltersEqual(nextFilters, sanitised)) {
            return {
                filters: sanitised,
                options: nextOptions,
            };
        }

        nextFilters = sanitised;
    }
};

const getCropLabel = (crop) => {
    if (!crop) {
        return "";
    }

    return crop.id === NULL_FILTER_TOKEN ? "No crop" : crop.name;
};

const formatDate = (value) => {
    if (!value) {
        return "-";
    }

    const dateValue = new Date(value);
    if (Number.isNaN(dateValue.getTime())) {
        return value;
    }

    return new Intl.DateTimeFormat("en-AU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    }).format(dateValue);
};

const formatConditionReportValue = (value) => {
    if (value === null || value === undefined || value === "") {
        return "-";
    }

    return numberFormat(value);
};

const formatConditionReportTank = (item) => {
    const lineNumber = Number(item?.lineNumber);
    if (Number.isFinite(lineNumber) && lineNumber > 0) {
        return `Tank ${lineNumber}`;
    }

    return "No tank";
};

const formatConditionReportPositions = (item) => {
    const start = Number(item?.startPosition);
    const end = Number(item?.endPosition);
    const hasStart = Number.isFinite(start) && start > 0;
    const hasEnd = Number.isFinite(end) && end > 0;

    if (hasStart && hasEnd) {
        return start === end ? `${start}` : `${start}-${end}`;
    }

    if (hasStart) {
        return `${start}`;
    }

    return "-";
};

const buildConditionReportGroups = (conditionReports) => {
    const sortedReports = [...conditionReports].sort((left, right) => {
        const leftLine = Number(left?.lineNumber);
        const rightLine = Number(right?.lineNumber);
        const leftLineSort = Number.isFinite(leftLine) ? leftLine : Number.MAX_SAFE_INTEGER;
        const rightLineSort = Number.isFinite(rightLine) ? rightLine : Number.MAX_SAFE_INTEGER;

        if (leftLineSort !== rightLineSort) {
            return leftLineSort - rightLineSort;
        }

        const leftStart = Number(left?.startPosition);
        const rightStart = Number(right?.startPosition);
        const leftStartSort = Number.isFinite(leftStart) ? leftStart : Number.MAX_SAFE_INTEGER;
        const rightStartSort = Number.isFinite(rightStart) ? rightStart : Number.MAX_SAFE_INTEGER;

        if (leftStartSort !== rightStartSort) {
            return leftStartSort - rightStartSort;
        }

        const leftEnd = Number(left?.endPosition);
        const rightEnd = Number(right?.endPosition);
        const leftEndSort = Number.isFinite(leftEnd) ? leftEnd : Number.MAX_SAFE_INTEGER;
        const rightEndSort = Number.isFinite(rightEnd) ? rightEnd : Number.MAX_SAFE_INTEGER;

        if (leftEndSort !== rightEndSort) {
            return leftEndSort - rightEndSort;
        }

        return new Date(right?.date || 0).getTime() - new Date(left?.date || 0).getTime();
    });

    return sortedReports.reduce((tankGroups, item) => {
        const tankLabel = formatConditionReportTank(item);
        const positionLabel = formatConditionReportPositions(item);
        let tankGroup = tankGroups.find((group) => group.label === tankLabel);

        if (!tankGroup) {
            tankGroup = { label: tankLabel, positions: [] };
            tankGroups.push(tankGroup);
        }

        let positionGroup = tankGroup.positions.find((group) => group.label === positionLabel);
        if (!positionGroup) {
            positionGroup = { label: positionLabel, reports: [] };
            tankGroup.positions.push(positionGroup);
        }

        positionGroup.reports.push(item);
        return tankGroups;
    }, []);
};

const formatQty = (value, inDoz, isApprox = false) => {
    const prefix = isApprox ? "≈" : "";
    return `${prefix}${numberFormat(formatOysterMateQuantity(Number(value) || 0, inDoz, false))}`;
};

const leaseDisplayName = (lease) => {
    if (!lease) {
        return "Unknown Lease";
    }

    if (lease.leaseNumber) {
        return `${lease.leaseName} (${lease.leaseNumber})`;
    }

    return lease.leaseName;
};

// Palette for grade-based stacked bar datasets (cycles if more grades than colours).
const GRADE_PALETTE = [
    ["rgba(25,118,210,0.75)", "rgba(25,118,210,1)"],
    ["rgba(46,125,50,0.75)", "rgba(46,125,50,1)"],
    ["rgba(239,108,0,0.75)", "rgba(239,108,0,1)"],
    ["rgba(123,31,162,0.75)", "rgba(123,31,162,1)"],
    ["rgba(211,47,47,0.75)", "rgba(211,47,47,1)"],
    ["rgba(0,137,123,0.75)", "rgba(0,137,123,1)"],
    ["rgba(245,127,23,0.75)", "rgba(245,127,23,1)"],
    ["rgba(63,81,181,0.75)", "rgba(63,81,181,1)"],
];

const buildLeaseChartDatasets = (leaseStages) => {
    // Collect all distinct grades across all leases, preserving encounter order.
    const gradeOrder = [];
    const gradeSeen = new Set();
    leaseStages.forEach((lease) => {
        (lease.gradeTotals || []).forEach(({ grade }) => {
            if (!gradeSeen.has(grade)) {
                gradeSeen.add(grade);
                gradeOrder.push(grade);
            }
        });
    });

    return gradeOrder.map((grade, index) => {
        const [bg, border] = GRADE_PALETTE[index % GRADE_PALETTE.length];
        return {
            label: grade || "Unknown Grade",
            data: leaseStages.map((lease) => {
                const match = (lease.gradeTotals || []).find((g) => g.grade === grade);
                return match ? Number(match.oysterCount) || 0 : 0;
            }),
            backgroundColor: bg,
            borderColor: border,
            borderWidth: 1,
            stack: "grades",
        };
    });
};

const buildLeaseChartDatasetsForDisplay = (leaseStages, inDoz) => {
    return buildLeaseChartDatasets(leaseStages).map((dataset) => ({
        ...dataset,
        data: dataset.data.map((value) => formatOysterMateQuantity(value, inDoz, false)),
    }));
};

const buildStageRows = (leaseStages, transferBreakdown, transferGradeTotals) => {
    const rows = Array.isArray(leaseStages) ? [...leaseStages] : [];
    const totalTransferred = Array.isArray(transferBreakdown)
        ? transferBreakdown.reduce((sum, item) => sum + (Number(item.oysterCount) || 0), 0)
        : 0;
    const transferGrades = Array.isArray(transferGradeTotals)
        ? transferGradeTotals
            .map((item) => ({
                gradeId: item.gradeId,
                grade: item.grade,
                oysterCount: Number(item.oysterCount) || 0,
            }))
            .filter((item) => item.oysterCount > 0)
        : [];

    if (totalTransferred > 0) {
        rows.push({
            leaseId: -999999,
            leaseName: "Transfers from Hatchery",
            leaseNumber: null,
            totalIntoLease: totalTransferred,
            gradeTotals: transferGrades.length > 0
                ? transferGrades
                : [{ gradeId: -1, grade: "Transferred", oysterCount: totalTransferred }],
        });
    }

    return rows;
};

const HatcheryLeaseChart = ({ leaseStages, transferBreakdown, transferGradeTotals, loading, inDoz }) => {
    const canvasRef = React.useRef(null);
    const chartRef = React.useRef(null);
    const stageRows = React.useMemo(
        () => buildStageRows(leaseStages, transferBreakdown, transferGradeTotals),
        [leaseStages, transferBreakdown, transferGradeTotals],
    );

    React.useEffect(() => {
        if (!canvasRef.current || loading || !Array.isArray(stageRows) || stageRows.length === 0) {
            if (chartRef.current) {
                chartRef.current.destroy();
                chartRef.current = null;
            }
            return;
        }

        let isMounted = true;

        const renderChart = async () => {
            const chartModule = await import("chart.js/auto");
            const Chart = chartModule.default;

            if (!isMounted || !canvasRef.current) {
                return;
            }

            if (chartRef.current) {
                chartRef.current.destroy();
            }

            const datasets = buildLeaseChartDatasetsForDisplay(stageRows, inDoz);

            chartRef.current = new Chart(canvasRef.current, {
                type: "bar",
                data: {
                    labels: stageRows.map((lease) => leaseDisplayName(lease)),
                    datasets,
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    indexAxis: "y",
                    plugins: {
                        legend: {
                            position: "top",
                        },
                    },
                    scales: {
                        x: {
                            stacked: true,
                            title: {
                                display: true,
                                text: inDoz ? "Dozen" : "Oyster Count",
                            },
                        },
                        y: {
                            stacked: true,
                        },
                    },
                },
            });
        };

        renderChart();

        return () => {
            isMounted = false;
            if (chartRef.current) {
                chartRef.current.destroy();
                chartRef.current = null;
            }
        };
    }, [stageRows, loading, inDoz]);

    if (!Array.isArray(stageRows) || stageRows.length === 0) {
        return <Typography variant="body2" color="text.secondary">No lease stage data found for this selection.</Typography>;
    }

    return (
        <Box sx={{ height: Math.max(180, stageRows.length * 48 + 60) }}>
            <canvas ref={canvasRef} />
        </Box>
    );
};

const ConditionReportsTable = ({ conditionReports, keyPrefix }) => {
    const [expandedTanks, setExpandedTanks] = React.useState({});
    const [expandedPositions, setExpandedPositions] = React.useState({});
    const groupedReports = buildConditionReportGroups(conditionReports);
    const toggleTank = (tankKey) => {
        setExpandedTanks((current) => ({ ...current, [tankKey]: !current[tankKey] }));
    };
    const togglePosition = (positionKey) => {
        setExpandedPositions((current) => ({ ...current, [positionKey]: !current[positionKey] }));
    };

    return (
        <TableContainer>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Tank</TableCell>
                        <TableCell>Position</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell align="right">Temp</TableCell>
                        <TableCell align="right">Salinity</TableCell>
                        <TableCell align="right">pH</TableCell>
                        <TableCell align="right">Gut %</TableCell>
                        <TableCell align="right">Length</TableCell>
                        <TableCell align="right">Density</TableCell>
                        <TableCell>Oil Droplets</TableCell>
                        <TableCell>Notes</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {groupedReports.map((tankGroup) => {
                        const tankKey = `${keyPrefix}-${tankGroup.label}`;
                        const isTankExpanded = !!expandedTanks[tankKey];

                        return (
                            <React.Fragment key={tankKey}>
                                <TableRow hover sx={{ cursor: "pointer" }} onClick={() => toggleTank(tankKey)}>
                                    <TableCell colSpan={11} sx={{ backgroundColor: "#f5f5f5", fontWeight: 700 }}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <IconButton size="small" aria-label={isTankExpanded ? "Collapse tank" : "Expand tank"}>
                                                <ExpandMoreIcon
                                                    fontSize="small"
                                                    sx={{
                                                        transform: isTankExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                                                        transition: "transform 0.15s",
                                                    }}
                                                />
                                            </IconButton>
                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                {tankGroup.label} ({tankGroup.positions.length} position{tankGroup.positions.length === 1 ? "" : "s"})
                                            </Typography>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                                {isTankExpanded ? tankGroup.positions.map((positionGroup) => {
                                    const positionKey = `${tankKey}-${positionGroup.label}`;
                                    const isPositionExpanded = !!expandedPositions[positionKey];

                                    return (
                                        <React.Fragment key={positionKey}>
                                            <TableRow hover sx={{ cursor: "pointer" }} onClick={() => togglePosition(positionKey)}>
                                                <TableCell colSpan={11} sx={{ backgroundColor: "#fafafa", fontWeight: 700, pl: 3 }}>
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <IconButton size="small" aria-label={isPositionExpanded ? "Collapse position" : "Expand position"}>
                                                            <ExpandMoreIcon
                                                                fontSize="small"
                                                                sx={{
                                                                    transform: isPositionExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                                                                    transition: "transform 0.15s",
                                                                }}
                                                            />
                                                        </IconButton>
                                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                            Position {positionGroup.label} ({positionGroup.reports.length} report{positionGroup.reports.length === 1 ? "" : "s"})
                                                        </Typography>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                            {isPositionExpanded ? positionGroup.reports.map((item) => (
                                                <TableRow key={`${keyPrefix}-${item.id}`}>
                                                    <TableCell>{formatConditionReportTank(item)}</TableCell>
                                                    <TableCell>{formatConditionReportPositions(item)}</TableCell>
                                                    <TableCell>{formatDate(item.date)}</TableCell>
                                                    <TableCell align="right">{formatConditionReportValue(item.temperature)}</TableCell>
                                                    <TableCell align="right">{formatConditionReportValue(item.salinity)}</TableCell>
                                                    <TableCell align="right">{formatConditionReportValue(item.ph)}</TableCell>
                                                    <TableCell align="right">{formatConditionReportValue(item.gutContentPercent)}</TableCell>
                                                    <TableCell align="right">{formatConditionReportValue(item.length)}</TableCell>
                                                    <TableCell align="right">{formatConditionReportValue(item.density)}</TableCell>
                                                    <TableCell>{item.oilDropletsCondition || "-"}</TableCell>
                                                    <TableCell>{item.notes || "-"}</TableCell>
                                                </TableRow>
                                            )) : null}
                                        </React.Fragment>
                                    );
                                }) : null}
                            </React.Fragment>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const renderConditionReportsTable = (conditionReports, keyPrefix, emptyMessage) => {
    if (!Array.isArray(conditionReports) || conditionReports.length === 0) {
        return <Typography variant="body2" color="text.secondary">{emptyMessage}</Typography>;
    }

    return <ConditionReportsTable conditionReports={conditionReports} keyPrefix={keyPrefix} />;
};

const HatcheryReport = ({ access }) => {
    const [lightboxImage, setLightboxImage] = React.useState(null);
    const [inDoz, setInDoz] = React.useState(false);
    const [farm, setFarm] = React.useState(null);
    const [filters, setFilters] = React.useState(emptyFilters);
    const [filterBatches, setFilterBatches] = React.useState(emptyFilterBatches);
    const [report, setReport] = React.useState(emptyReport);
    const [showLeaseChart, setShowLeaseChart] = React.useState(true);
    const [loadingFilters, setLoadingFilters] = React.useState(false);
    const [loadingReport, setLoadingReport] = React.useState(false);
    const [liveGroups, setLiveGroups] = React.useState([]);
    const [liveLoading, setLiveLoading] = React.useState(false);
    const [liveError, setLiveError] = React.useState(null);
    const [liveLoadedAt, setLiveLoadedAt] = React.useState(null);
    const [hasSubmitted, setHasSubmitted] = React.useState(false);
    const [error, setError] = React.useState("");
    const resolvedFilterState = React.useMemo(
        () => resolveFilterState(filterBatches, filters),
        [filterBatches, filters],
    );
    const activeFilters = resolvedFilterState.filters;
    const filterOptions = resolvedFilterState.options;

    const liveRows = React.useMemo(() => buildHatcheryLiveStockRows(liveGroups), [liveGroups]);
    const liveGradeColourMap = React.useMemo(() => {
        const colours = {};
        let nextIndex = 0;

        liveRows.forEach((row) => {
            row.grade.forEach((grade) => {
                if (!colours[grade.description]) {
                    colours[grade.description] = getOysterMateGradeColor(grade, nextIndex);
                    nextIndex += 1;
                }
            });
        });

        return colours;
    }, [liveRows]);

    const liveTotalOysters = React.useMemo(() => {
        return liveRows.reduce((sum, row) => sum + row.totalEnd, 0);
    }, [liveRows]);

    const liveMaxTotal = React.useMemo(() => {
        return liveRows.reduce((max, row) => Math.max(max, row.totalEnd), 0);
    }, [liveRows]);

    const liveHasApproxTotal = React.useMemo(() => {
        return liveRows.some((row) => row.grade.some((grade) => grade.description === SLATS));
    }, [liveRows]);

    const liveLoadedAtLabel = React.useMemo(() => {
        if (!liveLoadedAt) {
            return "";
        }

        return `${liveLoadedAt.toLocaleDateString("en-AU")} ${liveLoadedAt.toLocaleTimeString("en-AU", {
            hour: "2-digit",
            minute: "2-digit",
        })}`;
    }, [liveLoadedAt]);

    React.useEffect(() => {
        const abortController = new AbortController();

        if (!farm?.warehouseId) {
            setFilterBatches(emptyFilterBatches);
            setFilters(emptyFilters);
            setReport(emptyReport);
            setLiveGroups([]);
            setLiveError(null);
            setLiveLoadedAt(null);
            setHasSubmitted(false);
            setError("");
            setLiveLoading(false);
            return () => abortController.abort();
        }

        setLoadingFilters(true);
        setFilterBatches(emptyFilterBatches);
        setFilters(emptyFilters);
        setReport(emptyReport);
        setLiveGroups([]);
        setLiveError(null);
        setLiveLoadedAt(null);
        setLiveLoading(false);
        setHasSubmitted(false);
        setError("");

        getAPIDataParamsReturn(
            "/Farms/BatchReporting/HatcheryReportFilters",
            { WarehouseId: farm.warehouseId },
            abortController,
        )
            .then((data) => {
                setFilterBatches(normaliseBatchOptions(data?.batches));
            })
            .catch(() => {
                setFilterBatches(emptyFilterBatches);
                setError("Unable to load Hatchery Report filters.");
            })
            .finally(() => setLoadingFilters(false));

        return () => abortController.abort();
    }, [farm?.warehouseId]);

    React.useEffect(() => {
        if (!farm?.warehouseId || !hasSubmitted) {
            setLiveGroups([]);
            setLiveError(null);
            setLiveLoadedAt(null);
            setLiveLoading(false);
            return undefined;
        }

        const abortController = new AbortController();
        let isActive = true;

        const loadLiveStock = async () => {
            setLiveLoading(true);
            setLiveError(null);

            try {
                const groups = await fetchHatcheryLiveStockGroups(farm.warehouseId, activeFilters, abortController);
                if (!isActive) {
                    return;
                }

                setLiveGroups(groups);
                setLiveLoadedAt(new Date());
            } catch (error) {
                if (!isActive || abortController.signal.aborted || error?.name === "AbortError") {
                    return;
                }

                console.error(error);
                setLiveGroups([]);
                setLiveError(error);
            } finally {
                if (isActive) {
                    setLiveLoading(false);
                }
            }
        };

        loadLiveStock();

        return () => {
            isActive = false;
            abortController.abort();
        };
    }, [
        farm?.warehouseId,
        hasSubmitted,
        activeFilters,
    ]);

    React.useEffect(() => {
        const filtersMatch = (
            filters.cropId === activeFilters.cropId
            && filters.supplierId === activeFilters.supplierId
            && filters.stockType === activeFilters.stockType
            && filters.cropDate === activeFilters.cropDate
        );

        if (filtersMatch) {
            return;
        }

        setFilters({
            cropId: activeFilters.cropId,
            supplierId: activeFilters.supplierId,
            stockType: activeFilters.stockType,
            cropDate: activeFilters.cropDate,
        });
    }, [
        filters.cropId,
        filters.supplierId,
        filters.stockType,
        filters.cropDate,
        activeFilters.cropId,
        activeFilters.supplierId,
        activeFilters.stockType,
        activeFilters.cropDate,
    ]);

    const handleFilterChange = (field) => (event) => {
        const nextValue = event.target.value;
        setFilters((current) => ({ ...current, [field]: nextValue }));
    };

    const handleSubmit = async () => {
        if (!farm?.warehouseId) {
            return;
        }

        setLoadingReport(true);
        setHasSubmitted(true);
        setError("");
        setShowLeaseChart(true);

        try {
            const data = await getAPIDataParamsReturn("/Farms/BatchReporting/HatcheryReport", {
                WarehouseId: farm.warehouseId,
                CropId: activeFilters.cropId || undefined,
                SupplierId: activeFilters.supplierId || undefined,
                StockType: activeFilters.stockType || undefined,
                CropDate: activeFilters.cropDate || undefined,
            });

            setReport({
                selectedGroupCount: Number(data?.selectedGroupCount) || 0,
                broodstock: Array.isArray(data?.broodstock) ? data.broodstock : [],
                leaseStages: Array.isArray(data?.leaseStages) ? data.leaseStages : [],
                onshoreStage: data?.onshoreStage || null,
                transferBreakdown: Array.isArray(data?.transferBreakdown) ? data.transferBreakdown : [],
                transferGradeTotals: Array.isArray(data?.transferGradeTotals) ? data.transferGradeTotals : [],
            });

            setLiveGroups([]);
        }
        catch {
            setReport(emptyReport);
            setError("Unable to load the Hatchery Report.");
        }
        finally {
            setLoadingReport(false);
        }
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} md={6} lg={5}>
                <SelectFarmWarehouse
                    setState={setFarm}
                    state={farm}
                    access={access}
                    title="Hatchery Warehouse"
                    filterWarehouse={(warehouse) => warehouse?.isHatchery === true}
                />
            </Grid>

            <Grid item xs={12} md={6} lg={7}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent={{ xs: "flex-start", md: "flex-end" }}>
                    <Typography variant="body2">Pcs</Typography>
                    <Switch checked={inDoz} onChange={(e) => setInDoz(e.target.checked)} size="small" />
                    <Typography variant="body2">Doz</Typography>
                </Stack>
            </Grid>

            <Grid item xs={12}>
                <Card>
                    <CardHeader
                        title="Hatchery Report"
                        subheader="Filter hatchery batches, then load lease-stage in/out totals, broodstock details, and transfer breakdowns."
                    />
                    <CardContent>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Crop"
                                    value={activeFilters.cropId}
                                    onChange={handleFilterChange("cropId")}
                                    disabled={!farm?.warehouseId || loadingFilters || loadingReport}
                                >
                                    <MenuItem value="">All crops</MenuItem>
                                    {filterOptions.crops.map((crop) => (
                                        <MenuItem key={crop.id} value={crop.id}>{getCropLabel(crop)}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} md={6} lg={3}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Supplier"
                                    value={activeFilters.supplierId}
                                    onChange={handleFilterChange("supplierId")}
                                    disabled={!farm?.warehouseId || loadingFilters || loadingReport}
                                >
                                    <MenuItem value="">All suppliers</MenuItem>
                                    {filterOptions.suppliers.map((option) => (
                                        <MenuItem key={option.id} value={option.id}>{option.name}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} md={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Stock Type"
                                    value={activeFilters.stockType}
                                    onChange={handleFilterChange("stockType")}
                                    disabled={!farm?.warehouseId || loadingFilters || loadingReport}
                                >
                                    <MenuItem value="">All stock types</MenuItem>
                                    {filterOptions.stockTypes.map((option) => (
                                        <MenuItem key={option.id} value={option.id}>{option.name}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} md={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Crop Date"
                                    value={activeFilters.cropDate}
                                    onChange={handleFilterChange("cropDate")}
                                    disabled={!farm?.warehouseId || loadingFilters || loadingReport}
                                >
                                    <MenuItem value="">All crop dates</MenuItem>
                                    {filterOptions.cropDates.map((option) => (
                                        <MenuItem key={option} value={option}>{formatDate(option)}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} md={6} lg={1}>
                                <Button
                                    variant="contained"
                                    fullWidth
                                    sx={{ minHeight: 56 }}
                                    onClick={handleSubmit}
                                    disabled={!farm?.warehouseId || loadingFilters || loadingReport}
                                >
                                    {loadingReport ? "Loading..." : "Submit"}
                                </Button>
                            </Grid>
                        </Grid>

                        {loadingFilters ? (
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
                                <CircularProgress size={18} />
                                <Typography variant="body2">Loading warehouse filters...</Typography>
                            </Stack>
                        ) : null}

                        {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}

                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
                            Use the same batch-selection filters as Batch Report to scope hatchery lease movement and broodstock data.
                        </Typography>
                    </CardContent>
                </Card>
            </Grid>

            <Grid item xs={12}>
                <Card>
                    <CardHeader
                        title="Broodstock Used"
                        subheader={hasSubmitted
                            ? `Selected groups: ${numberFormat(report.selectedGroupCount)}`
                            : "Load the Hatchery Report to view broodstock details for this crop."}
                    />
                    <CardContent>
                        {loadingReport ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <CircularProgress size={20} />
                                <Typography variant="body2">Loading broodstock details...</Typography>
                            </Stack>
                        ) : report.broodstock.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                {hasSubmitted
                                    ? "No broodstock records were linked to this crop selection."
                                    : "No data loaded yet."}
                            </Typography>
                        ) : (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Spat Group</TableCell>
                                            <TableCell>Broodstock Group</TableCell>
                                            <TableCell>Gender</TableCell>
                                            <TableCell>Crop</TableCell>
                                            <TableCell>Supplier</TableCell>
                                            <TableCell>Grade</TableCell>
                                            <TableCell align="right">Units</TableCell>
                                            <TableCell align="right">Average</TableCell>
                                            <TableCell align="right">Oyster Count</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {report.broodstock.map((item) => (
                                            <TableRow key={`${item.spatGroupId}-${item.broodstockGroupId}-${item.gender}`}>
                                                <TableCell>{item.spatGroupId}</TableCell>
                                                <TableCell>{item.broodstockGroupId}</TableCell>
                                                <TableCell>{item.gender}</TableCell>
                                                <TableCell>{item.crop}</TableCell>
                                                <TableCell>{item.supplier}</TableCell>
                                                <TableCell>{item.grade}</TableCell>
                                                <TableCell align="right">{numberFormat(item.totalUnits)}</TableCell>
                                                <TableCell align="right">{formatQty(item.average, inDoz)}</TableCell>
                                                <TableCell align="right">{formatQty(item.oysterCount, inDoz)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </CardContent>
                </Card>
            </Grid>

            <Grid item xs={12}>
                <Card>
                    <CardHeader
                        title="Current Live Stock"
                        subheader={
                            liveError
                                ? "Unable to load live Oyster Mate totals."
                                : liveLoadedAt
                                    ? `Live Oyster Mate totals refreshed ${liveLoadedAtLabel}`
                                    : "Live Oyster Mate totals"
                        }
                        action={
                            <Stack spacing={0.25} alignItems={{ xs: "flex-start", sm: "flex-end" }}>
                                <Typography variant="body2" color="text.secondary" mb={0}>
                                    Active batches
                                </Typography>
                                <Typography variant="h5" mb={0}>
                                    {liveRows.length}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" mb={0}>
                                    {(liveHasApproxTotal ? "≈" : "") + numberFormat(formatOysterMateQuantity(liveTotalOysters, inDoz, false))} {inDoz ? "doz" : "pcs"}
                                </Typography>
                            </Stack>
                        }
                    />
                    <CardContent>
                        {loadingReport || liveLoading ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <CircularProgress size={20} />
                                <Typography variant="body2">Loading live stock...</Typography>
                            </Stack>
                        ) : liveError ? (
                            <Typography color="error" variant="body2">
                                The live totals could not be loaded for the selected farm and filters.
                            </Typography>
                        ) : liveRows.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                No live batches were found for the current selection.
                            </Typography>
                        ) : (
                            <Stack spacing={2}>
                                {liveRows.map((row) => {
                                    const grades = [...row.grade].sort((a, b) => (a.description || "").localeCompare(b.description || ""));
                                    const hasApprox = grades.some((grade) => grade.description === SLATS);

                                    return (
                                        <Box
                                            key={row.batch || "unknown"}
                                            sx={{
                                                backgroundColor: getBatchBackgroundColor(row.batch),
                                                border: "1px solid rgba(0, 0, 0, 0.08)",
                                                borderRadius: 2,
                                                p: 2,
                                            }}
                                        >
                                            <Grid container spacing={2} alignItems="center">
                                                <Grid item xs={12} md={3}>
                                                    <Typography variant="h6" mb={0}>
                                                        {row.batch && row.batch.trim() !== "" ? row.batch : "Unknown"}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary" mb={0}>
                                                        {grades.length} grade{grades.length === 1 ? "" : "s"}
                                                        {hasApprox ? " · includes slat estimate" : ""}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} md={7}>
                                                    <Box
                                                        sx={{
                                                            height: 28,
                                                            overflow: "hidden",
                                                            borderRadius: 999,
                                                            backgroundColor: "rgba(255, 255, 255, 0.8)",
                                                            border: "1px solid rgba(0, 0, 0, 0.1)",
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                height: "100%",
                                                                width: `${liveMaxTotal > 0 ? (row.totalEnd / liveMaxTotal) * 100 : 0}%`,
                                                                minWidth: row.totalEnd > 0 ? 12 : 0,
                                                            }}
                                                        >
                                                            {grades.map((grade) => {
                                                                const width = row.totalEnd > 0 ? (grade.end / row.totalEnd) * 100 : 0;

                                                                return (
                                                                    <Box
                                                                        key={`${row.batch || "unknown"}-${grade.description}`}
                                                                        sx={{
                                                                            width: `${width}%`,
                                                                            minWidth: width > 0 ? 4 : 0,
                                                                            backgroundColor: liveGradeColourMap[grade.description],
                                                                        }}
                                                                        title={`${grade.description}: ${(grade.description === SLATS ? "≈" : "") + numberFormat(formatOysterMateQuantity(grade.end, inDoz, false))} ${inDoz ? "doz" : "pcs"}`}
                                                                    />
                                                                );
                                                            })}
                                                        </Box>
                                                    </Box>
                                                    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                                                        {grades.map((grade) => (
                                                            <Stack
                                                                key={`${row.batch || "unknown"}-${grade.description}-legend`}
                                                                direction="row"
                                                                spacing={0.75}
                                                                alignItems="center"
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        width: 10,
                                                                        height: 10,
                                                                        borderRadius: "50%",
                                                                        backgroundColor: liveGradeColourMap[grade.description],
                                                                        border: "1px solid rgba(0, 0, 0, 0.12)",
                                                                    }}
                                                                />
                                                                <Typography variant="caption" mb={0}>
                                                                    {grade.description}: {(grade.description === SLATS ? "≈" : "") + numberFormat(formatOysterMateQuantity(grade.end, inDoz, false))}
                                                                </Typography>
                                                            </Stack>
                                                        ))}
                                                    </Stack>
                                                </Grid>
                                                <Grid item xs={12} md={2}>
                                                    <Typography variant="h5" mb={0} align="right">
                                                        {(hasApprox ? "≈" : "") + numberFormat(formatOysterMateQuantity(row.totalEnd, inDoz, false))}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary" mb={0} align="right">
                                                        {inDoz ? "doz" : "pcs"}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    );
                                })}
                            </Stack>
                        )}
                    </CardContent>
                </Card>
            </Grid>

            <Grid item xs={12}>
                <Card>
                    <CardHeader
                        title="Total Entered into Each Stage"
                        subheader="Total In reflects movement into each lease (entry events), not current oysters remaining at that lease."
                        action={report.leaseStages.length > 0 ? (
                            <Button variant="outlined" onClick={() => setShowLeaseChart((current) => !current)} disabled={loadingReport}>
                                {showLeaseChart ? "Show Table" : "Show Chart"}
                            </Button>
                        ) : null}
                    />
                    <CardContent>
                        {loadingReport ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <CircularProgress size={20} />
                                <Typography variant="body2">Loading lease stage totals...</Typography>
                            </Stack>
                        ) : showLeaseChart ? (
                            <HatcheryLeaseChart
                                leaseStages={report.leaseStages}
                                transferBreakdown={report.transferBreakdown}
                                transferGradeTotals={report.transferGradeTotals}
                                loading={loadingReport}
                                inDoz={inDoz}
                            />
                        ) : report.leaseStages.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                {hasSubmitted ? "No lease stage totals were found." : "No data loaded yet."}
                            </Typography>
                        ) : (
                            (() => {
                                const stageRows = buildStageRows(report.leaseStages, report.transferBreakdown, report.transferGradeTotals);
                                const gradeColumns = stageRows.reduce((acc, row) => {
                                    (row.gradeTotals || []).forEach((g) => {
                                        if (!acc.some((existing) => existing.gradeId === g.gradeId)) {
                                            acc.push({ gradeId: g.gradeId, grade: g.grade });
                                        }
                                    });
                                    return acc;
                                }, []);
                                return (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Lease</TableCell>
                                            <TableCell align="right">Total In</TableCell>
                                            {gradeColumns.map((g) => (
                                                <TableCell key={g.gradeId} align="right">{g.grade || "Unknown"}</TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {stageRows.map((lease) => (
                                            <TableRow key={`lease-stage-${lease.leaseId}`}>
                                                <TableCell>{leaseDisplayName(lease)}</TableCell>
                                                <TableCell align="right">{formatQty(lease.totalIntoLease, inDoz)}</TableCell>
                                                {gradeColumns.map((column) => {
                                                    const match = (lease.gradeTotals || []).find((g) => g.gradeId === column.gradeId);
                                                    return (
                                                        <TableCell key={`${lease.leaseId}-${column.gradeId}`} align="right">
                                                            {formatQty(match?.oysterCount || 0, inDoz)}
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                                );
                            })()
                        )}
                    </CardContent>
                </Card>
            </Grid>

            <Grid item xs={12}>
                <Card>
                    <CardHeader title="Stage Details, Condition Reports, and Images" />
                    <CardContent>
                        {loadingReport ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <CircularProgress size={20} />
                                <Typography variant="body2">Loading lease details...</Typography>
                            </Stack>
                        ) : report.leaseStages.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                {hasSubmitted
                                    ? "No lease-stage details were found for this selection."
                                    : "No data loaded yet."}
                            </Typography>
                        ) : (
                            <Stack spacing={1.5}>
                                {report.leaseStages.map((lease) => (
                                    <Accordion key={`lease-accordion-${lease.leaseId}`}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: "wrap" }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                    {leaseDisplayName(lease)}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Into: {formatQty(lease.totalIntoLease, inDoz)}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Out: {formatQty(lease.totalOutOfLease, inDoz)}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Groups: {numberFormat(lease.groups?.length || 0)}
                                                </Typography>
                                            </Stack>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12}>
                                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Condition Reports In This Lease Stage</Typography>
                                                    {renderConditionReportsTable(
                                                        lease.conditionReports,
                                                        `lease-report-${lease.leaseId}`,
                                                        "No condition reports found for this stage."
                                                    )}
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Images In This Lease Stage</Typography>
                                                    {lease.images?.length > 0 ? (
                                                        <Grid container spacing={1}>
                                                            {lease.images.map((image) => (
                                                                <Grid item xs={6} sm={4} key={`lease-image-${lease.leaseId}-${image.id}`}>
                                                                    <Box
                                                                        onClick={() => setLightboxImage(image.imageUrl)}
                                                                        sx={{ cursor: "pointer", textDecoration: "none", color: "inherit" }}
                                                                    >
                                                                        <Box
                                                                            component="img"
                                                                            src={image.imageUrl}
                                                                            alt={`Group ${image.groupId}`}
                                                                            sx={{
                                                                                width: "100%",
                                                                                height: 120,
                                                                                objectFit: "cover",
                                                                                borderRadius: 1,
                                                                                border: "1px solid #d0d0d0",
                                                                                transition: "opacity 0.15s",
                                                                                "&:hover": { opacity: 0.8 }
                                                                            }}
                                                                        />
                                                                        <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
                                                                            {formatDate(image.date)}
                                                                        </Typography>
                                                                    </Box>
                                                                </Grid>
                                                            ))}
                                                        </Grid>
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">No images found for this stage.</Typography>
                                                    )}
                                                </Grid>
                                            </Grid>
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                            </Stack>
                        )}
                    </CardContent>
                </Card>
            </Grid>

            <Grid item xs={12}>
                <Card>
                    <CardHeader title="Onshore" subheader="Groups that are currently onshore in the hatchery warehouse." />
                    <CardContent>
                        {loadingReport ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <CircularProgress size={20} />
                                <Typography variant="body2">Loading onshore data...</Typography>
                            </Stack>
                        ) : !report.onshoreStage ? (
                            <Typography variant="body2" color="text.secondary">
                                {hasSubmitted ? "No groups are currently onshore for this selection." : "No data loaded yet."}
                            </Typography>
                        ) : (
                            <Stack spacing={1}>
                                <Stack direction="row" spacing={2} flexWrap="wrap">
                                    <Typography variant="body2">Into: <strong>{formatQty(report.onshoreStage.totalIntoLease, inDoz)}</strong></Typography>
                                    <Typography variant="body2">Out: <strong>{formatQty(report.onshoreStage.totalOutOfLease, inDoz)}</strong></Typography>
                                    <Typography variant="body2">Groups: <strong>{numberFormat(report.onshoreStage.groups?.length || 0)}</strong></Typography>
                                </Stack>
                                {report.onshoreStage.groups?.length > 0 ? (
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Group</TableCell>
                                                    <TableCell>Crop</TableCell>
                                                    <TableCell>Grade</TableCell>
                                                    <TableCell align="right">Oyster Count</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {report.onshoreStage.groups.map((group) => (
                                                    <TableRow key={`onshore-group-${group.groupId}`}>
                                                        <TableCell>{group.groupId}</TableCell>
                                                        <TableCell>{group.cropLabel}</TableCell>
                                                        <TableCell>{group.grade}</TableCell>
                                                        <TableCell align="right">{formatQty(group.oysterCount, inDoz)}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                ) : null}
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Condition Reports Onshore</Typography>
                                    {renderConditionReportsTable(
                                        report.onshoreStage.conditionReports,
                                        "onshore-report",
                                        "No condition reports found for onshore groups."
                                    )}
                                </Box>
                            </Stack>
                        )}
                    </CardContent>
                </Card>
            </Grid>

            <Dialog
                open={!!lightboxImage}
                onClose={() => setLightboxImage(null)}
                maxWidth="lg"
                PaperProps={{ sx: { bgcolor: "#000", boxShadow: "none", position: "relative" } }}
            >
                <IconButton
                    onClick={() => setLightboxImage(null)}
                    size="small"
                    sx={{ position: "absolute", top: 8, right: 8, color: "#fff", zIndex: 1, bgcolor: "rgba(0,0,0,0.5)", "&:hover": { bgcolor: "rgba(0,0,0,0.7)" } }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
                <DialogContent sx={{ p: 0 }}>
                    {lightboxImage ? (
                        <Box
                            component="img"
                            src={lightboxImage}
                            alt="Expanded image"
                            sx={{ display: "block", maxWidth: "90vw", maxHeight: "85vh", objectFit: "contain" }}
                        />
                    ) : null}
                </DialogContent>
            </Dialog>

        </Grid>
    );
};

export default HatcheryReport;

